export const AMOUNT_MODE_INSTALLMENT = 'installment';
export const AMOUNT_MODE_WHOLE = 'whole_pledge';

function toCents(value) {
    return Math.round((Number(value) || 0) * 100);
}

export function money(value) {
    return toCents(value) / 100;
}

export function lastInstallmentAmount(remainingBalance, installmentAmount, numLeft) {
    const total = money(remainingBalance);
    const per = money(installmentAmount);
    const count = parseInt(numLeft, 10) || 0;
    if (count < 2 || per <= 0) {
        return per;
    }
    return money(total - per * (count - 1));
}

function installmentsNeeded(balance, installment) {
    const balanceCents = toCents(balance);
    const installmentCents = toCents(installment);
    if (installmentCents <= 0 || balanceCents <= 0) {
        return 0;
    }

    return Math.trunc((balanceCents + installmentCents - 1) / installmentCents);
}

function remainingKeepsInstallment(remaining, installment) {
    if (money(remaining) <= 0) {
        return true;
    }
    const amountCents = toCents(remaining);
    const installmentCents = toCents(installment);
    if (installmentCents <= 0 || amountCents < installmentCents) {
        return false;
    }
    return amountCents % installmentCents === 0;
}

export function canKeepInstallment(remaining, installment) {
    return remainingKeepsInstallment(remaining, installment) || money(remaining) < money(installment);
}

export function maxPaymentsForMinimum(remainingBalance, minInstallment) {
    const balanceCents = toCents(remainingBalance);
    const minCents = toCents(minInstallment);
    if (minCents <= 0 || balanceCents <= 0) {
        return 0;
    }
    return Math.trunc(balanceCents / minCents);
}

export function recurringSchedule(mode, remainingBalance, numLeft, installmentAmount, capToNumLeft = false) {
    const balance = money(remainingBalance);
    if (mode !== AMOUNT_MODE_INSTALLMENT) {
        return { numLeft, installmentAmount: numLeft === 1 ? balance : money(balance / numLeft) };
    }
    const installment = money(installmentAmount);
    const needed = installmentsNeeded(balance, installment);
    const count = capToNumLeft ? Math.min(numLeft, needed) : needed;
    return { numLeft: count, installmentAmount: count === 1 ? balance : installment };
}

function belowMinimumError(mode, minInstallment) {
    const amount = (Number(minInstallment) || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    const subject = mode === AMOUNT_MODE_INSTALLMENT ? 'The remaining installment' : 'Each remaining installment';
    return {
        error: `${subject} would be below the minimum payment amount of $${amount}.`,
        belowMinimum: true,
    };
}

export function planRecurringEdit(mode, remainingBalance, requestedNumLeft, currentInstallment, minInstallment) {
    const balance = money(remainingBalance);
    const installment = money(currentInstallment);
    if (balance <= 0) {
        return { error: 'There is no remaining balance to schedule.' };
    }
    if (mode === AMOUNT_MODE_INSTALLMENT) {
        if (installment <= 0) {
            return { error: 'The recurring schedule is missing a valid installment amount.' };
        }
        if (!remainingKeepsInstallment(balance, installment) && toCents(balance) >= toCents(installment)) {
            return {
                error: 'The remaining balance must be a whole number of scheduled payments to keep the payment amount the same.',
            };
        }
        const plan = recurringSchedule(mode, balance, requestedNumLeft, installment);
        if (toCents(plan.installmentAmount) < toCents(minInstallment)) {
            return belowMinimumError(mode, minInstallment);
        }
        return plan;
    }
    const count = parseInt(requestedNumLeft, 10);
    if (!Number.isInteger(count) || count < 1) {
        return { error: 'Please enter how many payments are left.' };
    }
    const plan = recurringSchedule(mode, balance, count, installment);
    if (toCents(plan.installmentAmount) < toCents(minInstallment)) {
        return belowMinimumError(mode, minInstallment);
    }
    return plan;
}
